import { CLOSE_UNAUTHORIZED } from "@gjwtrack/domain";
/**
 * One WebSocket that remembers what the client wants subscribed and re-sends
 * it after every reconnect. The M2 protocol has no "resume from version", so
 * a reconnect is a resubscribe: every view gets a fresh snapshot (M3-PLAN).
 */
export class LiveSocket {
    ws = null;
    desired = new Map();
    backoff;
    reconnectTimer = null;
    pingTimer = null;
    stopped = false;
    everOpened = false;
    _status = "closed";
    opts;
    constructor(opts) {
        this.opts = { pingMs: 25_000, minBackoffMs: 500, maxBackoffMs: 10_000, log: () => { }, ...opts };
        this.backoff = this.opts.minBackoffMs;
    }
    get status() {
        return this._status;
    }
    get isOpen() {
        return this.ws !== null && this.ws.readyState === 1;
    }
    start() {
        this.stopped = false;
        this.connect();
    }
    stop() {
        this.stopped = true;
        if (this.reconnectTimer)
            clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.stopPing();
        const ws = this.ws;
        this.ws = null;
        if (ws) {
            ws.onclose = null;
            ws.close(1000, "client stopped");
        }
        this.setStatus("closed");
    }
    subscribe(sub, query, limit) {
        this.desired.set(sub, { kind: "query", query, limit });
        this.sendNow({ t: "subscribe", sub, query, limit });
    }
    subscribeBoard(sub, boardId) {
        this.desired.set(sub, { kind: "board", boardId });
        this.sendNow({ t: "board", sub, boardId });
    }
    unsubscribe(sub) {
        if (!this.desired.delete(sub))
            return;
        this.sendNow({ t: "unsubscribe", sub });
    }
    /** Subscriptions this socket will (re)establish. */
    get subscriptions() {
        return this.desired;
    }
    sendNow(msg) {
        if (this.isOpen)
            this.ws.send(JSON.stringify(msg));
    }
    setStatus(s) {
        if (this._status === s)
            return;
        this._status = s;
        this.opts.onStatus?.(s);
    }
    connect() {
        if (this.stopped)
            return;
        this.setStatus("connecting");
        let ws;
        try {
            ws = this.opts.createSocket();
        }
        catch (e) {
            this.opts.log(`connect failed: ${e.message}`);
            this.scheduleReconnect();
            return;
        }
        this.ws = ws;
        ws.onopen = () => {
            if (this.ws !== ws)
                return;
            this.backoff = this.opts.minBackoffMs;
            this.setStatus("open");
            if (this.everOpened)
                this.opts.onReconnect?.();
            this.everOpened = true;
            for (const [sub, d] of this.desired) {
                if (d.kind === "query")
                    ws.send(JSON.stringify({ t: "subscribe", sub, query: d.query, limit: d.limit }));
                else
                    ws.send(JSON.stringify({ t: "board", sub, boardId: d.boardId }));
            }
            this.startPing();
        };
        ws.onmessage = (ev) => {
            if (this.ws !== ws)
                return;
            let msg;
            try {
                msg = JSON.parse(String(ev.data));
            }
            catch {
                this.opts.log("unparseable message");
                return;
            }
            if (msg.t === "pong")
                return;
            this.opts.onMessage(msg);
        };
        ws.onclose = (ev) => {
            if (this.ws !== ws)
                return;
            this.ws = null;
            this.stopPing();
            if (ev.code === CLOSE_UNAUTHORIZED) {
                this.setStatus("unauthorized");
                return;
            }
            this.setStatus("closed");
            this.scheduleReconnect();
        };
        ws.onerror = () => {
            /* onclose follows; nothing to do here */
        };
    }
    scheduleReconnect() {
        if (this.stopped || this.reconnectTimer)
            return;
        const delay = this.backoff;
        this.backoff = Math.min(this.backoff * 2, this.opts.maxBackoffMs);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }
    startPing() {
        this.stopPing();
        this.pingTimer = setInterval(() => this.sendNow({ t: "ping" }), this.opts.pingMs);
    }
    stopPing() {
        if (this.pingTimer)
            clearInterval(this.pingTimer);
        this.pingTimer = null;
    }
}
//# sourceMappingURL=socket.js.map